import { Slider as ChakraSlider, HStack } from '@chakra-ui/react';
import { forwardRef } from 'react';

import Tooltip from 'ui/Tooltip';

type SliderSize = 'sm' | 'md';

const trackSizeProps: Record<SliderSize, Partial<ChakraSlider.TrackProps>> = {
  sm: { h: '4px' },
  md: { h: '5px' },
};

const thumbSizeProps: Record<SliderSize, Partial<ChakraSlider.ThumbProps>> = {
  sm: { boxSize: '14px' },
  md: { boxSize: '18px' },
};

export interface SliderProps extends ChakraSlider.RootProps {
  size?: SliderSize;
  marks?: Array<number | { value: number; label: React.ReactNode }>;
  label?: React.ReactNode;
  showValue?: boolean;
}

const Slider = forwardRef<HTMLDivElement, SliderProps>(
  ({ marks: marksProp, label, showValue, size = 'md', ...rest }, ref) => {
    const marks = marksProp?.map((mark) => {
      if (typeof mark === 'number') return { value: mark, label: undefined };
      return mark;
    });

    const value = rest.defaultValue ?? rest.value;
    const hasMarkLabel = !!marks?.some((mark) => mark.label);

    return (
      <ChakraSlider.Root
        ref={ref}
        cursor="pointer"
        thumbAlignment="center"
        size={size}
        {...rest}
      >
        {label && !showValue && (
          <ChakraSlider.Label>{label}</ChakraSlider.Label>
        )}
        {label && showValue && (
          <HStack justify="space-between">
            <ChakraSlider.Label>{label}</ChakraSlider.Label>
            <ChakraSlider.ValueText />
          </HStack>
        )}
        <ChakraSlider.Control data-has-mark-label={hasMarkLabel || undefined}>
          <ChakraSlider.Track {...trackSizeProps[size]}>
            <ChakraSlider.Range background="green.800" />
          </ChakraSlider.Track>
          {value?.map((val, index) => (
            <Tooltip
              showArrow
              key={index}
              content={val}
              openDelay={0}
              closeDelay={0}
              closeOnPointerDown={false}
              positioning={{ placement: 'top' }}
            >
              <ChakraSlider.Thumb
                index={index}
                border="2px solid"
                borderColor="green.700"
                _focusVisible={{ ringColor: 'blue.500' }}
                {...thumbSizeProps[size]}
              >
                <ChakraSlider.HiddenInput />
              </ChakraSlider.Thumb>
            </Tooltip>
          ))}
          <SliderMarks marks={marks} />
        </ChakraSlider.Control>
      </ChakraSlider.Root>
    );
  }
);

interface SliderMarksProps {
  marks?: Array<number | { value: number; label: React.ReactNode }>;
}

const SliderMarks = forwardRef<HTMLDivElement, SliderMarksProps>(
  ({ marks }, ref) => {
    if (!marks?.length) return null;

    return (
      <ChakraSlider.MarkerGroup ref={ref}>
        {marks.map((mark, index) => {
          const value = typeof mark === 'number' ? mark : mark.value;
          const label = typeof mark === 'number' ? undefined : mark.label;
          return (
            <ChakraSlider.Marker key={index} value={value}>
              <ChakraSlider.MarkerIndicator />
              {label}
            </ChakraSlider.Marker>
          );
        })}
      </ChakraSlider.MarkerGroup>
    );
  }
);

export default Slider;
